<script>
/**
 * @component SeguimientoTab
 * @description Tab de seguimiento del paciente.
 * Muestra: barra de protocolo, stats cards, alertas activas,
 * adherencia a medicación con cuadraditos de color,
 * signos vitales con sparklines expandibles, actividad reciente.
 */
import { formatDistanceStrict, parseISO } from "date-fns";
import dateFnsEsp from "date-fns/locale/es";
import format from "date-fns/format";
import { TASKS_TYPES } from "@/services/constants";
import api from "@/services/api";
import { mapGetters } from "vuex";
import LabVitalsTable from "@/components/patient/dashboard/LabVitalsTable.vue";
// UC-REG-065: rediseño de Seguimiento — KPIs header + TimelineCronico
// reemplaza la sección "Actividad reciente" lineal vieja + panel de
// conversaciones IA expandible al costado del Timeline.
import TimelineCronico from "@/components/patient/dashboard/TimelineCronico.vue";
import ConversacionesIaPanel from "@/components/patient/dashboard/ConversacionesIaPanel.vue";
import HealthScoreBadge from "@/components/ui/HealthScoreBadge.vue";
import healthConversationTaskService from "@/services/health-conversation-task";
import monitoringService, { trendIndicator } from "@/services/monitoring";

export default {
  name: "SeguimientoTab",
  components: {
    LabVitalsTable,
    TimelineCronico,
    ConversacionesIaPanel,
    HealthScoreBadge,
  },

  props: {
    /** @type {Object} Objeto paciente completo */
    patient: { type: Object, required: true },
    /** @type {Object} Vitals agrupados por nombre { 'Presión': [...], 'FC': [...] } */
    vitals: { type: Object, default: () => ({}) },
    /** @type {Object} Alertas agrupadas por tipo { 'medications': [...], 'checkins': [...] } */
    alerts: { type: Object, default: () => ({}) },
    /** @type {Array} Alertas de actividad sin verificar */
    activityAlerts: { type: Array, default: () => [] },
    /** @type {Object} Vitales de laboratorio para tabla */
    labVitals: { type: Object, default: () => ({}) },
  },

  data() {
    return {
      expandedVital: null,
      completingMedId: null,
      /** @description Tab activo del seguimiento */
      activeTab: "alertas",
      // UC-REG-065: state del rediseño
      monitoring: null,
      monitoringLoading: false,
      healthConversations: [],
      healthConversationsLoading: false,
    };
  },

  created() {
    this.$bus.$on("seguimiento-go-to-lab", () => {
      this.activeTab = "laboratorio";
    });
    this.loadMonitoring();
    this.loadHealthConversations();
  },

  beforeDestroy() {
    this.$bus.$off("seguimiento-go-to-lab");
  },

  computed: {
    ...mapGetters(["isPatient"]),

    /**
     * @description Protocolo asignado del paciente (treatment)
     * @returns {Object|null}
     */
    protocol() {
      const treatment = this.patient?.patient?.treatment;
      if (!treatment || !treatment.name) return null;
      return treatment;
    },

    /**
     * @description Conteo de alertas por color (red, yellow, green)
     * @returns {{ red: number, yellow: number, green: number }}
     */
    alertCounts() {
      let red = 0;
      let yellow = 0;
      let green = 0;

      this.activityAlerts.forEach((alert) => {
        if (alert.completedMeasurement === "red" || alert.shouldBeCompleted)
          red++;
        else if (alert.completedMeasurement === "yellow") yellow++;
        else if (alert.completedMeasurement === "green") green++;
      });

      Object.values(this.alerts).forEach((alertGroup) => {
        alertGroup.forEach((alert) => {
          if (alert.completedMeasurement === "red" || alert.shouldBeCompleted)
            red++;
          else if (alert.completedMeasurement === "yellow") yellow++;
          else if (alert.completedMeasurement === "green") green++;
        });
      });

      return { red, yellow, green };
    },

    /**
     * @description Porcentaje de adherencia general
     * @returns {number}
     */
    adherencePercent() {
      const { red, yellow, green } = this.alertCounts;
      const total = red + yellow + green;
      if (total === 0) return 100;
      return Math.round((green / total) * 100);
    },

    /**
     * @description Alertas activas ordenadas (red primero, luego yellow), máx 5
     * @returns {Array}
     */
    activeAlerts() {
      const colorOrder = { red: 0, yellow: 1 };
      const allAlerts = [...this.activityAlerts]
        .filter(
          (a) =>
            a.completedMeasurement === "red" ||
            a.completedMeasurement === "yellow" ||
            a.shouldBeCompleted
        )
        .sort((a, b) => {
          const aColor = a.shouldBeCompleted ? "red" : a.completedMeasurement;
          const bColor = b.shouldBeCompleted ? "red" : b.completedMeasurement;
          return (colorOrder[aColor] || 2) - (colorOrder[bColor] || 2);
        });
      return allAlerts.slice(0, 5);
    },

    /**
     * @description Total alertas activas (sin verificar, red/yellow)
     * @returns {number}
     */
    totalActiveAlerts() {
      return this.activityAlerts.filter(
        (a) =>
          a.completedMeasurement === "red" ||
          a.completedMeasurement === "yellow" ||
          a.shouldBeCompleted
      ).length;
    },

    /**
     * @description Datos de vitales formateados para las cards con sparklines
     * @returns {Array<Object>}
     */
    vitalCards() {
      return Object.entries(this.vitals).map(([name, readings]) => {
        const completed = readings.filter((r) => r.completedAt);
        const lastReading = completed[0];
        const values = completed
          .slice(0, 15)
          .reverse()
          .map((r) => {
            const answer = r.answerValues?.[0];
            const parsed = answer ? parseFloat(answer.value) : NaN;
            return {
              value: parsed,
              date: r.completedAt,
              measurement: r.completedMeasurement,
            };
          })
          .filter((v) => !isNaN(v.value) && v.value > 0);

        const lastValue = lastReading?.answerValues?.[0];
        const isOutOfRange =
          lastReading?.completedMeasurement === "red" ||
          lastReading?.completedMeasurement === "yellow";

        let trend = "stable";
        if (values.length >= 2) {
          const recent = values[values.length - 1].value;
          const previous = values[values.length - 2].value;
          if (recent > previous * 1.02) trend = "up";
          else if (recent < previous * 0.98) trend = "down";
        }

        return {
          name,
          lastValue: lastValue ? lastValue.value : "—",
          unit: lastValue ? lastValue.unit || "" : "",
          lastDate: lastReading?.completedAt,
          isOutOfRange,
          trend,
          measurement: lastReading?.completedMeasurement,
          values,
          readings: completed.slice(0, 10),
        };
      });
    },

    /**
     * @description Cards de medicación con adherencia, cuadraditos de color y porcentaje
     * @returns {Array<Object>}
     */
    medicationCards() {
      const meds = this.alerts.medications || [];
      if (meds.length === 0) return [];

      const grouped = {};
      meds.forEach((med) => {
        const key = med.name || "Sin nombre";
        if (!grouped[key]) {
          grouped[key] = {
            name: key,
            description: med.description || "",
            quantity: med.quantity || "",
            tasks: [],
          };
        }
        grouped[key].tasks.push(med);
      });

      return Object.values(grouped).map((group) => {
        const total = group.tasks.length;
        const completed = group.tasks.filter((t) => t.completedAt);
        const onTime = completed.filter(
          (t) => t.completedMeasurement === "green"
        ).length;
        const late = completed.filter(
          (t) => t.completedMeasurement === "yellow"
        ).length;
        const missed = group.tasks.filter(
          (t) =>
            !t.completedAt &&
            (t.completedMeasurement === "red" || t.shouldBeCompleted)
        ).length;
        const pending = total - completed.length - missed;
        const adherence =
          total > 0 ? Math.round((completed.length / total) * 100) : 100;

        const periodType = group.tasks[0]?.periodization?.type || "";
        const periodHours = group.tasks[0]?.periodization?.hours || [];
        let freqText = "";
        if (periodHours.length > 0) {
          freqText = `${periodHours.length} por día`;
          if (periodHours.length === 1 && periodType) {
            freqText = "1 por día";
          }
        }
        const durationText = periodType === "continuous" ? "Continuo" : "";
        const subtitle = [freqText, durationText].filter(Boolean).join(" · ");

        const sortedTasks = [...group.tasks].sort(
          (a, b) => new Date(a.scheduledAt) - new Date(b.scheduledAt)
        );

        const squares = sortedTasks.map((t) => {
          let color;
          if (t.completedAt) {
            color =
              t.completedMeasurement === "green"
                ? "green"
                : t.completedMeasurement === "yellow"
                ? "yellow"
                : "red";
          } else if (t.shouldBeCompleted || t.completedMeasurement === "red") {
            color = "red";
          } else {
            color = "pending";
          }
          return { color, task: t };
        });

        // Próxima toma pendiente que ya pasó su scheduledAt
        const now = new Date();
        const nextPending = sortedTasks.find(
          (t) =>
            !t.completedAt &&
            !t.shouldBeCompleted &&
            t.completedMeasurement !== "red" &&
            new Date(t.scheduledAt) <= now
        );

        const borderColor =
          adherence >= 90 ? "normal" : adherence >= 60 ? "warning" : "danger";

        return {
          name: group.name,
          quantity: group.quantity,
          subtitle,
          total,
          completed: completed.length,
          onTime,
          late,
          missed,
          pending,
          adherence,
          squares,
          borderColor,
          tasks: group.tasks,
          nextPending,
        };
      });
    },

    /**
     * @description Tarjetas de tareas por tipo (sin vitals ni medications que tienen sección propia)
     * @returns {Array<Object>}
     */
    alertGroups() {
      return Object.entries(TASKS_TYPES)
        .filter(([key]) => key !== "vitals" && key !== "medications")
        .map(([key, taskType]) => {
          const items = this.alerts[key] || [];
          return {
            key,
            name: taskType.name,
            total: items.length,
          };
        })
        .filter((g) => g.total > 0);
    },

    /**
     * @description Actividad reciente combinada de todos los tipos
     * @returns {Array}
     */
    recentActivity() {
      const allActivity = [];

      Object.entries(this.vitals).forEach(([name, readings]) => {
        readings
          .filter((r) => r.completedAt)
          .slice(0, 3)
          .forEach((r) => {
            allActivity.push({
              type: "vital",
              text: `Registro de ${name}`,
              value: r.answerValues?.[0]?.value || "",
              unit: r.answerValues?.[0]?.unit || "",
              date: r.completedAt,
            });
          });
      });

      Object.entries(this.alerts).forEach(([type, alertGroup]) => {
        alertGroup
          .filter((a) => a.completedAt)
          .slice(0, 2)
          .forEach((a) => {
            const typeName = TASKS_TYPES[type]?.name || type;
            allActivity.push({
              type,
              text: `${typeName}: ${a.name}`,
              date: a.completedAt,
            });
          });
      });

      return allActivity
        .sort((a, b) => new Date(b.date) - new Date(a.date))
        .slice(0, 8);
    },

    /**
     * @description UC-REG-065 — KPI: adherencia 7d % + flecha tendencia.
     * @returns {{ value: string, trendSymbol: string, trendClass: string }}
     */
    kpiAdherence7d() {
      const adh = this.monitoring && this.monitoring.adherence;
      const seven = adh && adh.sevenDays;
      if (!seven || seven.percentage == null)
        return { value: "—", trendSymbol: "", trendClass: "" };
      const ind = trendIndicator(seven.trend);
      return {
        value: `${seven.percentage}%`,
        trendSymbol: ind.symbol,
        trendClass: ind.className,
      };
    },

    /**
     * @description UC-REG-065 — KPI: adherencia 30d.
     */
    kpiAdherence30d() {
      const adh = this.monitoring && this.monitoring.adherence;
      const thirty = adh && adh.thirtyDays;
      if (!thirty || thirty.percentage == null)
        return { value: "—", trendSymbol: "", trendClass: "" };
      const ind = trendIndicator(thirty.trend);
      return {
        value: `${thirty.percentage}%`,
        trendSymbol: ind.symbol,
        trendClass: ind.className,
      };
    },

    /**
     * @description UC-REG-065 — KPI: último score IA.
     */
    kpiLastConversation() {
      const lc = this.monitoring && this.monitoring.lastConversation;
      if (!lc || lc.score == null) return null;
      return {
        score: lc.score,
        semaphore: lc.semaphore,
        templateName: lc.name,
      };
    },

    /**
     * @description UC-REG-065 — top 3 signals de la última healthConversation.
     */
    kpiTopSignals() {
      const lc = this.monitoring && this.monitoring.lastConversation;
      return (lc && lc.topSignals) || [];
    },

    /**
     * @description UC-REG-065 — activity stream para el timeline.
     */
    timelineActivity() {
      return (this.monitoring && this.monitoring.activity) || [];
    },
  },

  methods: {
    /**
     * @description UC-REG-065 — carga datos de monitoreo en background.
     */
    async loadMonitoring() {
      if (!this.patient || !this.patient._id) return;
      this.monitoringLoading = true;
      try {
        const { data } = await monitoringService.getPatientMonitoring(
          this.patient._id
        );
        this.monitoring = data;
      } catch (e) {
        // Si el endpoint falla (ej. paciente sin tasks), seguimos sin monitoring data.
        this.monitoring = null;
      } finally {
        this.monitoringLoading = false;
      }
    },

    /**
     * @description UC-REG-065 — carga las healthConversations del paciente.
     */
    async loadHealthConversations() {
      if (!this.patient || !this.patient._id) return;
      this.healthConversationsLoading = true;
      try {
        const res = await healthConversationTaskService.history("patient", {
          patient: this.patient._id,
          limit: 20,
          sort: { completedAt: -1 },
        });
        // history returns paginated { docs, total } shape
        this.healthConversations =
          (res.data && res.data.docs) || res.docs || [];
      } catch (e) {
        this.healthConversations = [];
      } finally {
        this.healthConversationsLoading = false;
      }
    },

    /**
     * @description UC-REG-065 — handler de click en evento del timeline.
     *              Por ahora abre el ModalTaskDetail; en futuro lo hacemos
     *              drawer dedicado por tipo.
     */
    onTimelineEventClick(event) {
      this.$bus.$emit("seguimiento-event-click", event);
    },

    /**
     * @description Formatea distancia temporal relativa
     * @param {string} date - Fecha ISO
     * @returns {string}
     */
    timeAgo(date) {
      if (!date) return "";
      try {
        return formatDistanceStrict(new Date(), parseISO(date), {
          addSuffix: true,
          locale: dateFnsEsp,
        });
      } catch {
        return "";
      }
    },

    /**
     * @description Formatea fecha corta
     * @param {string} date - Fecha ISO
     * @returns {string}
     */
    formatDate(date) {
      if (!date) return "";
      try {
        return format(new Date(date), "dd/MM HH:mm");
      } catch {
        return "";
      }
    },

    /**
     * @description Toggle expandir/colapsar vital card
     * @param {string} name - Nombre del vital
     */
    toggleVital(name) {
      this.expandedVital = this.expandedVital === name ? null : name;
    },

    /**
     * @description Genera SVG path suavizado con curvas Bézier para sparkline
     * @param {Array<Object>} values
     * @param {number} w - Ancho del SVG
     * @param {number} h - Alto del SVG
     * @returns {string}
     */
    sparklinePath(values, w = 120, h = 32) {
      if (!values || values.length < 2) return "";
      const nums = values.map((v) => v.value);
      const min = Math.min(...nums);
      const max = Math.max(...nums);
      const range = max - min || 1;
      const p = 2;

      const points = values.map((v, i) => ({
        x: (i / (values.length - 1)) * (w - p * 2) + p,
        y: h - ((v.value - min) / range) * (h - p * 2) - p,
      }));

      let d = `M ${points[0].x.toFixed(1)} ${points[0].y.toFixed(1)}`;
      for (let i = 1; i < points.length; i++) {
        const prev = points[i - 1];
        const curr = points[i];
        const tension = 0.3;
        const dx = curr.x - prev.x;
        const cp1x = prev.x + dx * tension;
        const cp2x = curr.x - dx * tension;
        d += ` C ${cp1x.toFixed(1)} ${prev.y.toFixed(1)}, ${cp2x.toFixed(
          1
        )} ${curr.y.toFixed(1)}, ${curr.x.toFixed(1)} ${curr.y.toFixed(1)}`;
      }
      return d;
    },

    /**
     * @description Genera SVG fill area path para sparkline
     * @param {Array<Object>} values
     * @param {number} w
     * @param {number} h
     * @returns {string}
     */
    sparklineArea(values, w = 120, h = 32) {
      if (!values || values.length < 2) return "";
      const path = this.sparklinePath(values, w, h);
      const lastX = ((values.length - 1) / (values.length - 1)) * (w - 4) + 2;
      return `${path} L ${lastX.toFixed(1)} ${h} L 2 ${h} Z`;
    },

    /**
     * @description Posición del último punto en sparkline
     * @param {Array<Object>} values
     * @param {number} w
     * @param {number} h
     * @returns {{ x: number, y: number }}
     */
    lastPoint(values, w = 120, h = 32) {
      if (!values || values.length < 2) return { x: 0, y: 0 };
      const nums = values.map((v) => v.value);
      const min = Math.min(...nums);
      const max = Math.max(...nums);
      const range = max - min || 1;
      const i = values.length - 1;
      const x = (i / (values.length - 1)) * (w - 4) + 2;
      const y = h - ((values[i].value - min) / range) * (h - 4) - 2;
      return { x, y };
    },

    /**
     * @description Color del vital según su measurement
     * @param {Object} vital
     * @returns {string}
     */
    vitalColor(vital) {
      return vital.isOutOfRange ? "#ef4444" : "#0d9d9d";
    },

    /**
     * @description Texto de tendencia
     * @param {Object} vital
     * @returns {string}
     */
    trendText(vital) {
      if (vital.trend === "up")
        return vital.isOutOfRange ? "↑ Alto" : "↑ Subiendo";
      if (vital.trend === "down")
        return vital.isOutOfRange ? "↓ Bajo" : "↓ Bajando";
      return "→ Estable";
    },

    /**
     * @description Color de tendencia
     * @param {Object} vital
     * @returns {string}
     */
    trendColor(vital) {
      if (vital.trend === "up" && vital.isOutOfRange) return "#ef4444";
      if (vital.trend === "down" && !vital.isOutOfRange) return "#22c55e";
      return "#94a3b8";
    },

    /**
     * @description Barra proporcional para readings
     * @param {number} value
     * @param {Array} values
     * @returns {number}
     */
    barPercent(value, values) {
      if (!values || values.length === 0) return 0;
      const nums = values.map((v) => v.value);
      const max = Math.max(...nums);
      return max > 0 ? (value / max) * 100 : 0;
    },

    /**
     * @description Extrae valor numérico del reading
     * @param {Object} reading
     * @returns {number}
     */
    readingValue(reading) {
      const v = reading.answerValues?.[0]?.value;
      return v ? parseFloat(v) : 0;
    },

    /**
     * @description Extrae valor display del reading
     * @param {Object} reading
     * @returns {string}
     */
    readingDisplay(reading) {
      const v = reading.answerValues?.[0]?.value;
      return v || "—";
    },

    /** @description Navega a tareas del paciente */
    goToTasks() {
      this.$router.push({ name: "patient-tasks" });
    },

    /**
     * @description Emite evento para ver detalle de alerta
     * @param {Object} alert
     */
    reviewAlert(alert) {
      this.$emit("show-modal", { modal: "task-detail", task: alert });
    },

    /**
     * @description Emite evento para agregar dato manual de vital
     * @param {string} name - Nombre del vital
     */
    addManualVital(name) {
      const vitalArray = this.vitals[name];
      const task =
        vitalArray && vitalArray.length > 0 ? vitalArray[0] : { name };
      this.$emit("show-modal", { modal: "add-manual-task", task });
    },

    /**
     * @description Emite evento para ver tabla de tareas
     * @param {string} type
     */
    viewTasksTable(type) {
      this.$emit("show-modal", { modal: "tasks-table", task: type });
    },

    /**
     * @description Completa una toma de medicación via API
     * @param {Object} med - Tarea de medicación
     */
    async completeMedication(med) {
      if (this.completingMedId || med.completedAt) return;
      this.completingMedId = med._id;
      try {
        await api.put(
          `/tasks/medications/${med._id}/patients/${this.patient._id}/complete`
        );
        med.completedAt = new Date().toISOString();
        med.completedMeasurement = "green";
        this.$forceUpdate();
      } catch (err) {
        const status = err?.response?.status;
        if (status === 400) {
          this.$message?.error?.(
            "Esta toma ya fue registrada o aún no es hora"
          );
        } else {
          this.$message?.error?.("Error al registrar la toma");
        }
      } finally {
        this.completingMedId = null;
      }
    },

    /**
     * @description Color de la alerta para el dot
     * @param {Object} alert
     * @returns {string}
     */
    alertDotColor(alert) {
      return alert.shouldBeCompleted ? "red" : alert.completedMeasurement;
    },

    /**
     * @description Color del borde de la card de medicación
     * @param {Object} card
     * @returns {string}
     */
    medBorderClass(card) {
      return `med-border-${card.borderColor}`;
    },

    /**
     * @description Color del badge de adherencia
     * @param {number} adherence
     * @returns {string}
     */
    adherenceBadgeClass(adherence) {
      if (adherence >= 90) return "adh-green";
      if (adherence >= 60) return "adh-yellow";
      return "adh-red";
    },
  },
};
</script>

<template lang="pug">
.seguimiento-tab
  //- Barra de protocolo — siempre visible
  .seg-protocol(v-if="protocol")
    .seg-protocol__left
      .seg-protocol__icon
        svg(width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="white" stroke-width="2" stroke-linecap="round" stroke-linejoin="round")
          path(d="M16 4h2a2 2 0 012 2v14a2 2 0 01-2 2H6a2 2 0 01-2-2V6a2 2 0 012-2h2")
          rect(x="8" y="2" width="8" height="4" rx="1")
      .seg-protocol__info
        p.seg-protocol__name {{ protocol.name }}
        p.seg-protocol__sub Asignado {{ protocol.assignedAt ? formatDate(protocol.assignedAt) : '' }}
    .seg-protocol__right
      button.seg-btn-secondary(@click="goToTasks") Ver tareas

  //- Semáforo — siempre visible
  .seg-stats(v-if="!isPatient")
    .seg-stat-card
      .seg-stat-card__top
        span.seg-stat-card__label Alertas rojas
        span.seg-stat-dot.red
      .seg-stat-card__value {{ alertCounts.red }}
    .seg-stat-card
      .seg-stat-card__top
        span.seg-stat-card__label Alertas amarillas
        span.seg-stat-dot.yellow
      .seg-stat-card__value {{ alertCounts.yellow }}
    .seg-stat-card
      .seg-stat-card__top
        span.seg-stat-card__label Normales
        span.seg-stat-dot.green
      .seg-stat-card__value {{ alertCounts.green }}
    .seg-stat-card
      .seg-stat-card__top
        span.seg-stat-card__label Adherencia %
        span.seg-stat-dot.teal
      .seg-stat-card__value {{ adherencePercent }}%

  //- Tabs
  .seg-tabs
    .seg-tab(
      :class="{ 'seg-tab--active': activeTab === 'alertas' }"
      @click="activeTab = 'alertas'"
    )
      | Alertas
      span.seg-tab__badge.seg-tab__badge--red(v-if="totalActiveAlerts > 0") {{ totalActiveAlerts }}
    .seg-tab(
      :class="{ 'seg-tab--active': activeTab === 'vitales' }"
      @click="activeTab = 'vitales'"
    )
      | Signos vitales
      span.seg-tab__badge(v-if="Object.keys(vitals).length > 0") {{ Object.keys(vitals).length }}
    .seg-tab(
      :class="{ 'seg-tab--active': activeTab === 'adherencia' }"
      @click="activeTab = 'adherencia'"
    ) Adherencia
    .seg-tab(
      :class="{ 'seg-tab--active': activeTab === 'laboratorio' }"
      @click="activeTab = 'laboratorio'"
    )
      | Laboratorio
      span.seg-tab__badge(v-if="Object.keys(labVitals).length > 0") {{ Object.keys(labVitals).length }}
    .seg-tab(
      :class="{ 'seg-tab--active': activeTab === 'actividad' }"
      @click="activeTab = 'actividad'"
    ) Actividad

  //- ── Tab: Alertas ──
  template(v-if="activeTab === 'alertas'")
    .seg-section(v-if="activeAlerts.length > 0")
      .seg-section__header
        .seg-section__title-row
          h3 Alertas activas
          span.seg-alert-count {{ totalActiveAlerts }}
        a.seg-link(@click="viewTasksTable('vitals')") Ver todas
      .seg-alert-list
        .seg-alert-row(
          v-for="(alert, idx) in activeAlerts"
          :key="alert._id || idx"
          @click="reviewAlert(alert)"
        )
          span.seg-alert-dot(:class="alertDotColor(alert)")
          .seg-alert-text
            span.seg-alert-title {{ alert.name }}
              span.seg-alert-value(v-if="alert.answerValues && alert.answerValues[0]")
                | &nbsp;— {{ alert.answerValues[0].value }} {{ alert.answerValues[0].unit || '' }}
            span.seg-alert-time {{ timeAgo(alert.completedAt || alert.updatedAt) }}
          button.seg-btn-mini(@click.stop="reviewAlert(alert)") Revisar
    .seg-section(v-if="alertGroups.length > 0")
      h3.seg-tasks-title Otras tareas
      .seg-task-row
        .seg-task-chip(
          v-for="group in alertGroups"
          :key="group.key"
        )
          span.seg-task-chip__dot
          span.seg-task-chip__name {{ group.name }}
          span.seg-task-chip__count {{ group.total }} tarea{{ group.total !== 1 ? 's' : '' }}
          a.seg-task-chip__link(@click="viewTasksTable(group.key)") Ver tareas
    .seg-empty(v-if="activeAlerts.length === 0 && alertGroups.length === 0")
      p No hay alertas activas.

  //- ── Tab: Adherencia ──
  template(v-if="activeTab === 'adherencia'")
    .seg-section(v-if="medicationCards.length > 0")
      .seg-section__header
        h3 Adherencia a medicación
        a.seg-link(@click="viewTasksTable('medications')") Ver detalle
      .seg-med-grid
        .seg-med-card(
          v-for="card in medicationCards"
          :key="card.name"
          :class="medBorderClass(card)"
        )
          .seg-med-card__top
            .seg-med-card__info
              p.seg-med-card__name {{ card.name }}
              p.seg-med-card__sub {{ card.subtitle }}
            span.seg-med-card__badge(:class="adherenceBadgeClass(card.adherence)") {{ card.adherence }}%
          .seg-med-card__squares
            span.seg-med-square(
              v-for="(sq, i) in card.squares"
              :key="i"
              :class="['sq-' + sq.color, { clickable: sq.color === 'pending' && !sq.task.completedAt && new Date(sq.task.scheduledAt) <= new Date() }]"
              :title="sq.color === 'pending' ? 'Click para registrar toma' : ''"
              @click.stop="sq.color === 'pending' && !sq.task.completedAt && new Date(sq.task.scheduledAt) <= new Date() ? completeMedication(sq.task) : null"
            )
              span.seg-med-square__check(v-if="completingMedId === sq.task._id") ···
          .seg-med-card__bottom
            span.seg-med-card__stats {{ card.completed }}/{{ card.total }} tomadas ·&nbsp;
              strong {{ card.missed }} omitidas
            .seg-med-card__actions
              button.seg-btn-med(
                v-if="card.nextPending"
                :disabled="!!completingMedId"
                @click.stop="completeMedication(card.nextPending)"
              ) {{ completingMedId === (card.nextPending && card.nextPending._id) ? 'Registrando...' : 'Registrar toma' }}
              a.seg-med-card__link(@click.stop="viewTasksTable('medications')") Detalle
      .seg-med-legend
        span.seg-med-legend__item
          span.seg-med-legend__sq.sq-green
          | A tiempo
        span.seg-med-legend__item
          span.seg-med-legend__sq.sq-yellow
          | Tarde
        span.seg-med-legend__item
          span.seg-med-legend__sq.sq-red
          | Omitida
        span.seg-med-legend__item
          span.seg-med-legend__sq.sq-pending
          | Pendiente
    .seg-empty(v-if="medicationCards.length === 0")
      p No hay medicación asignada.

  //- ── Tab: Signos vitales ──
  template(v-if="activeTab === 'vitales'")
    .seg-section(v-if="Object.keys(vitals).length > 0")
      .seg-section__header
        h3 Signos vitales
        a.seg-link(@click="viewTasksTable('vitals')") Historial completo
      .seg-vitals-grid
        .seg-vital-card(
          v-for="vital in vitalCards"
          :key="vital.name"
          :class="{ expanded: expandedVital === vital.name, 'out-of-range': vital.isOutOfRange }"
          @click="toggleVital(vital.name)"
        )
          .seg-vital-card__header
            span.seg-vital-card__label {{ vital.name }}
            span.seg-vital-card__trend(:style="{ color: trendColor(vital) }") {{ trendText(vital) }}
          .seg-vital-card__main
            span.seg-vital-card__value(:class="{ 'text-red': vital.isOutOfRange }") {{ vital.lastValue }}
            span.seg-vital-card__unit {{ vital.unit }}
          svg.seg-sparkline(
            v-if="vital.values.length >= 2"
            viewBox="0 0 120 32"
            preserveAspectRatio="none"
          )
            path.seg-sparkline__area(
              :d="sparklineArea(vital.values)"
              :fill="vitalColor(vital)"
              fill-opacity="0.08"
            )
            path.seg-sparkline__line(
              :d="sparklinePath(vital.values)"
              :stroke="vitalColor(vital)"
              fill="none"
              stroke-width="1.5"
              stroke-linecap="round"
              stroke-linejoin="round"
            )
            circle.seg-sparkline__dot(
              :cx="lastPoint(vital.values).x"
              :cy="lastPoint(vital.values).y"
              r="2.5"
              :fill="vitalColor(vital)"
            )
          .seg-vital-card__footer
            span.seg-vital-card__time {{ timeAgo(vital.lastDate) }}
            span.seg-vital-card__expand(v-if="expandedVital !== vital.name") Click para expandir
          .seg-vital-expanded(v-if="expandedVital === vital.name" @click.stop)
            .seg-vital-readings
              .seg-vital-reading(
                v-for="(reading, i) in vital.readings.slice(0, 8)"
                :key="i"
              )
                .seg-vital-reading__left
                  span.seg-vital-reading__date {{ formatDate(reading.completedAt) }}
                  span.seg-vital-reading__val(:class="{ 'is-red': reading.completedMeasurement === 'red', 'is-yellow': reading.completedMeasurement === 'yellow' }") {{ readingDisplay(reading) }}
                    span.seg-vital-reading__unit {{ reading.answerValues && reading.answerValues[0] ? reading.answerValues[0].unit || '' : '' }}
                .seg-vital-reading__bar
                  .seg-vital-reading__bar-fill(
                    :style="{ width: barPercent(readingValue(reading), vital.values) + '%' }"
                    :class="{ 'is-red': reading.completedMeasurement === 'red', 'is-yellow': reading.completedMeasurement === 'yellow', 'is-green': reading.completedMeasurement === 'green' || !reading.completedMeasurement }"
                  )
            .seg-vital-actions
              button.seg-btn-secondary(@click.stop="addManualVital(vital.name)") Cargar dato
    .seg-empty(v-if="Object.keys(vitals).length === 0")
      p No hay signos vitales registrados.

  //- ── Tab: Actividad ──
  //- UC-REG-065 — Tab Actividad rediseñado: KPIs row + TimelineCronico + panel
  //- de Conversaciones IA al costado. Reemplaza la lista lineal vieja.
  template(v-if="activeTab === 'actividad'")
    .seg-section
      //- KPIs row del paciente
      .v2-kpis
        .v2-kpi
          .v2-kpi__label Adherencia 7d
          .v2-kpi__value
            span {{ kpiAdherence7d.value }}
            span.v2-kpi__trend(:class="kpiAdherence7d.trendClass") {{ kpiAdherence7d.trendSymbol }}
        .v2-kpi
          .v2-kpi__label Adherencia 30d
          .v2-kpi__value
            span {{ kpiAdherence30d.value }}
            span.v2-kpi__trend(:class="kpiAdherence30d.trendClass") {{ kpiAdherence30d.trendSymbol }}
        .v2-kpi
          .v2-kpi__label Último score IA
          .v2-kpi__value(v-if="kpiLastConversation")
            HealthScoreBadge(
              :score="kpiLastConversation.score"
              :semaphore="kpiLastConversation.semaphore"
              size="md"
            )
          .v2-kpi__value(v-else)
            span —
          .v2-kpi__sub(v-if="kpiLastConversation") {{ kpiLastConversation.templateName }}
        .v2-kpi
          .v2-kpi__label Signals destacadas
          .v2-kpi__value
            .v2-signals(v-if="kpiTopSignals.length > 0")
              span.v2-signal(
                v-for="(s, idx) in kpiTopSignals"
                :key="`sig-${idx}`"
                :class="`v2-signal--${s.severity}`"
              ) {{ s.criterion }}
            span(v-else) —

      //- Layout 2-col: timeline a izquierda, panel conversaciones a derecha
      .v2-timeline-layout
        .v2-timeline-layout__main
          TimelineCronico(
            :activity="timelineActivity"
            @event-click="onTimelineEventClick"
          )
        .v2-timeline-layout__side
          ConversacionesIaPanel(
            :conversations="healthConversations"
            :loading="healthConversationsLoading"
          )

  //- ── Tab: Laboratorio ──
  template(v-if="activeTab === 'laboratorio'")
    lab-vitals-table(
      v-if="Object.keys(labVitals).length > 0"
      :vitals="labVitals"
    )
    .seg-empty(v-else)
      p No hay indicadores de laboratorio registrados.
</template>

<style lang="scss" scoped>
/* ═══════════════════════════════════════════════ */
/* VARIABLES                                       */
/* ═══════════════════════════════════════════════ */
$navy: #002d5e;
$teal: #0d9d9d;
$teal-bg: rgba(13, 157, 157, 0.06);
$gray-50: #f8fafc;
$gray-100: #f1f5f9;
$gray-200: #e2e8f0;
$gray-300: #cbd5e1;
$gray-400: #94a3b8;
$gray-500: #64748b;
$gray-600: #475569;
$gray-700: #334155;
$red: #ef4444;
$red-bg: rgba(239, 68, 68, 0.08);
$yellow: #f59e0b;
$green: #22c55e;

/* ═══════════════════════════════════════════════ */
/* CONTAINER                                       */
/* ═══════════════════════════════════════════════ */
.seguimiento-tab {
  padding: 4px 0;
  font-family: "Plus Jakarta Sans", "Nunito", sans-serif;
}

/* ═══════════════════════════════════════════════ */
/* 1. PROTOCOL BAR                                 */
/* ═══════════════════════════════════════════════ */
.seg-protocol {
  display: flex;
  align-items: center;
  justify-content: space-between;
  background: $gray-50;
  border: 1.5px solid $gray-200;
  border-radius: 8px;
  padding: 10px 16px;
  margin-bottom: 14px;

  &__left {
    display: flex;
    align-items: center;
    gap: 12px;
  }

  &__icon {
    width: 32px;
    height: 32px;
    border-radius: 6px;
    background: $navy;
    display: flex;
    align-items: center;
    justify-content: center;
    flex-shrink: 0;
  }

  &__name {
    font-size: 13px;
    font-weight: 700;
    color: $navy;
    margin: 0;
    line-height: 1.3;
  }

  &__sub {
    font-size: 11px;
    color: $gray-400;
    margin: 0;
  }
}

/* ═══════════════════════════════════════════════ */
/* 2. STATS CARDS                                  */
/* ═══════════════════════════════════════════════ */
.seg-stats {
  display: grid;
  grid-template-columns: repeat(4, 1fr);
  gap: 10px;
  margin-bottom: 18px;
}

.seg-stat-card {
  padding: 12px 14px;
  border: 1.5px solid $gray-200;
  border-radius: 8px;
  transition: border-color 0.15s;

  &:hover {
    border-color: $gray-300;
  }

  &__top {
    display: flex;
    justify-content: space-between;
    align-items: center;
    margin-bottom: 4px;
  }

  &__label {
    font-size: 10.5px;
    color: $gray-500;
    font-weight: 600;
  }

  &__value {
    font-size: 20px;
    font-weight: 800;
    color: $navy;
    line-height: 1;
  }
}

.seg-stat-dot {
  width: 7px;
  height: 7px;
  border-radius: 50%;
  &.red {
    background: $red;
  }
  &.yellow {
    background: $yellow;
  }
  &.green {
    background: $green;
  }
  &.teal {
    background: $teal;
  }
}

/* ═══════════════════════════════════════════════ */
/* SECTION COMMON                                  */
/* ═══════════════════════════════════════════════ */
.seg-section {
  margin-bottom: 18px;
}

.seg-section__header {
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-bottom: 10px;
  padding-bottom: 6px;
  border-bottom: 1px solid $gray-100;

  h3 {
    font-size: 13.5px;
    font-weight: 700;
    color: $navy;
    margin: 0;
  }

  .seg-section__title-row {
    display: flex;
    align-items: center;
    gap: 8px;
  }
}

.seg-link {
  font-size: 12px;
  font-weight: 700;
  color: $teal;
  cursor: pointer;
  text-decoration: none;
  &:hover {
    text-decoration: underline;
  }
}

.seg-alert-count {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  min-width: 18px;
  height: 18px;
  border-radius: 9px;
  background: $red-bg;
  color: $red;
  font-size: 9.5px;
  font-weight: 800;
  padding: 0 5px;
}

/* ═══════════════════════════════════════════════ */
/* 3. ALERTS                                       */
/* ═══════════════════════════════════════════════ */
.seg-alert-list {
  border: 1.5px solid $gray-200;
  border-radius: 8px;
  overflow: hidden;
}

.seg-alert-row {
  display: flex;
  align-items: center;
  gap: 10px;
  padding: 8px 12px;
  cursor: pointer;
  transition: background 0.12s;
  &:hover {
    background: $gray-50;
  }
  &:not(:last-child) {
    border-bottom: 1px solid $gray-100;
  }
}

.seg-alert-dot {
  width: 7px;
  height: 7px;
  border-radius: 50%;
  flex-shrink: 0;
  &.red {
    background: $red;
  }
  &.yellow {
    background: $yellow;
  }
  &.green {
    background: $green;
  }
}

.seg-alert-text {
  flex: 1;
  min-width: 0;
  display: flex;
  align-items: center;
  gap: 8px;
}

.seg-alert-title {
  font-size: 12.5px;
  font-weight: 700;
  color: $gray-700;
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
}

.seg-alert-value {
  font-weight: 600;
  color: $gray-500;
}

.seg-alert-time {
  font-size: 10px;
  color: $gray-400;
  white-space: nowrap;
  flex-shrink: 0;
}

.seg-btn-mini {
  font-size: 10.5px;
  font-weight: 700;
  color: $gray-500;
  background: none;
  border: 1.5px solid $gray-200;
  border-radius: 6px;
  padding: 3px 10px;
  cursor: pointer;
  white-space: nowrap;
  transition: all 0.12s;
  flex-shrink: 0;
  &:hover {
    border-color: $teal;
    color: $teal;
  }
}

/* ═══════════════════════════════════════════════ */
/* 4. MEDICATION ADHERENCE                         */
/* ═══════════════════════════════════════════════ */
.seg-med-grid {
  display: grid;
  grid-template-columns: repeat(2, 1fr);
  gap: 10px;
  margin-bottom: 8px;
}

.seg-med-card {
  padding: 14px 16px;
  border: 2px solid $gray-200;
  border-radius: 10px;
  transition: border-color 0.15s;

  &.med-border-normal {
    border-color: $gray-200;
  }
  &.med-border-warning {
    border-color: $yellow;
  }
  &.med-border-danger {
    border-color: $red;
  }

  &__top {
    display: flex;
    justify-content: space-between;
    align-items: flex-start;
    margin-bottom: 8px;
  }

  &__info {
    flex: 1;
    min-width: 0;
  }

  &__name {
    font-size: 14px;
    font-weight: 700;
    color: $navy;
    margin: 0 0 2px 0;
    line-height: 1.3;
  }

  &__sub {
    font-size: 11.5px;
    color: $gray-500;
    margin: 0;
  }

  &__badge {
    font-size: 12px;
    font-weight: 800;
    padding: 3px 10px;
    border-radius: 12px;
    flex-shrink: 0;
    margin-left: 10px;

    &.adh-green {
      background: rgba(34, 197, 94, 0.12);
      color: #16a34a;
    }
    &.adh-yellow {
      background: rgba(245, 158, 11, 0.12);
      color: #d97706;
    }
    &.adh-red {
      background: rgba(239, 68, 68, 0.1);
      color: $red;
    }
  }

  &__squares {
    display: flex;
    gap: 3px;
    flex-wrap: wrap;
    margin-bottom: 8px;
  }

  &__bottom {
    display: flex;
    justify-content: space-between;
    align-items: center;
  }

  &__stats {
    font-size: 11px;
    color: $gray-500;

    strong {
      color: $gray-700;
      font-weight: 700;
    }
  }

  &__actions {
    display: flex;
    align-items: center;
    gap: 8px;
  }

  &__link {
    font-size: 12px;
    font-weight: 700;
    color: $teal;
    cursor: pointer;
    text-decoration: none;
    &:hover {
      text-decoration: underline;
    }
  }
}

.seg-btn-med {
  font-size: 10.5px;
  font-weight: 700;
  color: white;
  background: $teal;
  border: none;
  border-radius: 5px;
  padding: 4px 10px;
  cursor: pointer;
  font-family: inherit;
  transition: all 0.12s;
  white-space: nowrap;

  &:hover {
    background: darken($teal, 5%);
  }
  &:disabled {
    opacity: 0.5;
    cursor: not-allowed;
  }
}

.seg-med-square {
  width: 16px;
  height: 16px;
  border-radius: 3px;
  flex-shrink: 0;
  position: relative;
  display: inline-flex;
  align-items: center;
  justify-content: center;

  &.sq-green {
    background: rgba(34, 197, 94, 0.35);
  }
  &.sq-yellow {
    background: rgba(245, 158, 11, 0.4);
  }
  &.sq-red {
    background: rgba(239, 68, 68, 0.35);
  }
  &.sq-pending {
    background: $gray-100;
  }

  &.clickable {
    cursor: pointer;
    border: 1.5px dashed $gray-300;
    background: rgba(13, 157, 157, 0.06);
    transition: all 0.15s;

    &:hover {
      border-color: $teal;
      background: rgba(13, 157, 157, 0.15);
      transform: scale(1.15);
    }
  }

  &__check {
    font-size: 8px;
    color: $teal;
    font-weight: 800;
  }
}

.seg-med-legend {
  display: flex;
  justify-content: flex-end;
  gap: 14px;
  margin-top: 4px;

  &__item {
    display: flex;
    align-items: center;
    gap: 4px;
    font-size: 10.5px;
    color: $gray-500;
  }

  &__sq {
    width: 10px;
    height: 10px;
    border-radius: 2px;
    flex-shrink: 0;
  }
}

/* ═══════════════════════════════════════════════ */
/* 5. TASK TYPE CHIPS                              */
/* ═══════════════════════════════════════════════ */
.seg-tasks-title {
  font-size: 14px;
  font-weight: 600;
  color: #0f172a;
  margin: 0 0 12px 0;
}

.seg-task-row {
  display: flex;
  gap: 10px;
  overflow-x: auto;
  padding-bottom: 4px;

  &::-webkit-scrollbar {
    height: 3px;
  }
  &::-webkit-scrollbar-thumb {
    background: $gray-200;
    border-radius: 3px;
  }
}

.seg-task-chip {
  background: #fff;
  border: 1px solid $gray-200;
  border-radius: 12px;
  padding: 12px 16px;
  display: flex;
  align-items: center;
  gap: 8px;
  white-space: nowrap;
  flex-shrink: 0;
  transition: border-color 0.15s;
  &:hover {
    border-color: $gray-300;
  }

  &__dot {
    width: 8px;
    height: 8px;
    border-radius: 50%;
    background: #0d9488;
    flex-shrink: 0;
  }

  &__name {
    font-size: 13px;
    font-weight: 600;
    color: #0f172a;
  }

  &__count {
    font-size: 12px;
    color: $gray-500;
  }

  &__link {
    font-size: 12px;
    font-weight: 700;
    color: #0d9488;
    cursor: pointer;
    text-decoration: none;
    margin-left: 4px;
    &:hover {
      text-decoration: underline;
    }
  }
}

/* ═══════════════════════════════════════════════ */
/* 6. VITALS GRID + SPARKLINES                     */
/* ═══════════════════════════════════════════════ */
.seg-vitals-grid {
  display: grid;
  grid-template-columns: repeat(3, 1fr);
  gap: 10px;
}

.seg-vital-card {
  padding: 12px 14px;
  border: 1.5px solid $gray-200;
  border-radius: 8px;
  cursor: pointer;
  transition: all 0.15s;
  position: relative;

  &:hover {
    border-color: $gray-300;
    .seg-vital-card__expand {
      opacity: 1;
    }
  }

  &.out-of-range {
    border-color: rgba($red, 0.4);
  }

  &.expanded {
    grid-column: 1 / -1;
    cursor: default;
    border-color: $teal;
  }

  &__header {
    display: flex;
    justify-content: space-between;
    align-items: center;
    margin-bottom: 4px;
  }

  &__label {
    font-size: 10px;
    text-transform: uppercase;
    letter-spacing: 0.3px;
    color: $gray-400;
    font-weight: 700;
  }

  &__trend {
    font-size: 9.5px;
    font-weight: 700;
  }

  &__main {
    display: flex;
    align-items: baseline;
    gap: 4px;
    margin-bottom: 4px;
  }

  &__value {
    font-size: 18px;
    font-weight: 800;
    color: $navy;
    &.text-red {
      color: $red;
    }
  }

  &__unit {
    font-size: 10px;
    color: $gray-400;
  }

  &__footer {
    display: flex;
    justify-content: space-between;
    align-items: center;
    margin-top: 4px;
  }

  &__time {
    font-size: 9.5px;
    color: $gray-400;
  }

  &__expand {
    font-size: 9px;
    color: $gray-400;
    opacity: 0;
    transition: opacity 0.15s;
  }
}

.seg-sparkline {
  width: 100%;
  height: 32px;
  display: block;
}

/* ═══════════════════════════════════════════════ */
/* EXPANDED VITAL — CLEAN READINGS LIST            */
/* ═══════════════════════════════════════════════ */
.seg-vital-expanded {
  margin-top: 10px;
  padding-top: 10px;
  border-top: 1px solid $gray-100;
}

.seg-vital-readings {
  display: flex;
  flex-direction: column;
}

.seg-vital-reading {
  display: flex;
  align-items: center;
  gap: 12px;
  padding: 6px 0;

  &:not(:last-child) {
    border-bottom: 1px solid $gray-100;
  }

  &__left {
    display: flex;
    align-items: baseline;
    gap: 8px;
    width: 160px;
    flex-shrink: 0;
  }

  &__date {
    font-size: 10px;
    color: $gray-400;
    width: 72px;
    flex-shrink: 0;
  }

  &__val {
    font-size: 13px;
    font-weight: 700;
    color: $navy;
    &.is-red {
      color: $red;
    }
    &.is-yellow {
      color: $yellow;
    }
  }

  &__unit {
    font-size: 10px;
    font-weight: 400;
    color: $gray-400;
    margin-left: 2px;
  }

  &__bar {
    flex: 1;
    height: 4px;
    background: $gray-100;
    border-radius: 2px;
    overflow: hidden;
  }

  &__bar-fill {
    height: 100%;
    border-radius: 2px;
    transition: width 0.3s;
    &.is-green {
      background: $teal;
    }
    &.is-red {
      background: $red;
    }
    &.is-yellow {
      background: $yellow;
    }
  }
}

.seg-vital-actions {
  display: flex;
  gap: 8px;
  justify-content: flex-end;
  margin-top: 8px;
}

/* ═══════════════════════════════════════════════ */
/* 7. ACTIVITY TIMELINE                            */
/* ═══════════════════════════════════════════════ */
.seg-timeline {
  position: relative;
  padding-left: 20px;

  &::before {
    content: "";
    position: absolute;
    left: 4px;
    top: 6px;
    bottom: 6px;
    width: 1.5px;
    background: $gray-200;
  }
}

.seg-timeline__item {
  position: relative;
  padding: 6px 0;
  display: flex;
  flex-direction: column;
  &:not(:last-child) {
    margin-bottom: 2px;
  }
}

.seg-timeline__dot {
  position: absolute;
  left: -20px;
  top: 10px;
  width: 9px;
  height: 9px;
  border-radius: 50%;
  background: white;
  border: 2px solid $gray-300;
  z-index: 1;
  .first & {
    background: $teal;
    border-color: $teal;
  }
}

.seg-timeline__content {
  display: flex;
  justify-content: space-between;
  align-items: baseline;
  gap: 8px;
}

.seg-timeline__text {
  font-size: 12px;
  color: $gray-600;
  strong {
    color: $navy;
    font-weight: 700;
  }
}

.seg-timeline__val {
  font-weight: 600;
  color: $gray-500;
}

.seg-timeline__time {
  font-size: 10px;
  color: $gray-400;
  white-space: nowrap;
  flex-shrink: 0;
}

/* ═══════════════════════════════════════════════ */
/* BUTTONS                                         */
/* ═══════════════════════════════════════════════ */
.seg-btn-secondary {
  font-size: 12px;
  font-weight: 700;
  color: $teal;
  background: none;
  border: 1.5px solid $gray-200;
  border-radius: 6px;
  padding: 5px 14px;
  cursor: pointer;
  font-family: inherit;
  transition: all 0.12s;
  &:hover {
    border-color: $teal;
    background: $teal-bg;
  }
}

.seg-btn-primary {
  font-size: 12px;
  font-weight: 700;
  color: white;
  background: $teal;
  border: 1.5px solid $teal;
  border-radius: 6px;
  padding: 5px 14px;
  cursor: pointer;
  font-family: inherit;
  transition: all 0.12s;
  &:hover {
    background: darken($teal, 5%);
  }
}

/* ═══════════════════════════════════════════════ */
/* EMPTY STATE                                     */
/* ═══════════════════════════════════════════════ */
.seg-empty {
  text-align: center;
  padding: 40px 20px;
  p {
    font-size: 13px;
    color: $gray-400;
  }
}

/* ── Tabs de seguimiento ── */
.seg-tabs {
  display: flex;
  border-bottom: 2px solid $gray-200;
  margin-top: 20px;
  margin-bottom: 20px;
  gap: 0;
}

.seg-tab {
  display: flex;
  align-items: center;
  gap: 6px;
  padding: 9px 16px;
  font-size: 13px;
  font-weight: 500;
  color: $gray-400;
  cursor: pointer;
  border-bottom: 2px solid transparent;
  margin-bottom: -2px;
  transition: all 0.15s;
  white-space: nowrap;

  &:hover {
    color: $gray-600;
  }

  &--active {
    color: $navy;
    border-bottom-color: $navy;
  }

  &__badge {
    font-size: 10px;
    padding: 1px 6px;
    border-radius: 10px;
    font-weight: 600;
    background: $gray-100;
    color: $gray-500;

    &--red {
      background: rgba($red, 0.1);
      color: $red;
    }
  }
}

/* ═══════════════════════════════════════════════ */
/* UC-REG-065 — V2: KPIs + Timeline + Conversations  */
/* ═══════════════════════════════════════════════ */
.v2-kpis {
  display: grid;
  grid-template-columns: repeat(4, 1fr);
  gap: 12px;
  margin-bottom: 20px;
  @media (max-width: 1100px) {
    grid-template-columns: repeat(2, 1fr);
  }
}
.v2-kpi {
  background: white;
  border: 1px solid #e5e7eb;
  border-radius: 12px;
  padding: 14px 16px;

  &__label {
    font-size: 11px;
    color: #6b7280;
    font-weight: 600;
    text-transform: uppercase;
    letter-spacing: 0.5px;
    margin-bottom: 8px;
  }
  &__value {
    font-size: 24px;
    font-weight: 700;
    color: #0f1e36;
    display: flex;
    align-items: center;
    gap: 8px;
    line-height: 1.1;
    font-variant-numeric: tabular-nums;
  }
  &__trend {
    font-size: 14px;
    font-weight: 700;
    &.trend--up {
      color: #1f8a4c;
    }
    &.trend--down {
      color: #b3261e;
    }
    &.trend--flat {
      color: #9ca3af;
    }
    &.trend--none {
      color: #9ca3af;
    }
  }
  &__sub {
    font-size: 11px;
    color: #9ca3af;
    margin-top: 4px;
  }
}
.v2-signals {
  display: flex;
  flex-wrap: wrap;
  gap: 4px;
}
.v2-signal {
  font-size: 11px;
  padding: 2px 8px;
  border-radius: 4px;
  background: #f3f4f6;
  color: #6b7280;
  font-weight: 600;
  &--high {
    background: #fbe9e7;
    color: #b3261e;
  }
  &--mid {
    background: #fbf3e0;
    color: #b07c0a;
  }
  &--low {
    background: #ebf7f0;
    color: #1f8a4c;
  }
}
.v2-timeline-layout {
  display: grid;
  grid-template-columns: 1fr 380px;
  gap: 16px;
  @media (max-width: 1200px) {
    grid-template-columns: 1fr;
  }
}
</style>
